import { useState, useRef } from "react";
import { createPortal } from "react-dom";

/* Fix DY (2026-09-23): "En cualquier desplegable no debería faltar un buscador o
   filtro para agilizar las gestiones" (Edson, 23/09). Reemplaza a un <select> y
   guarda exactamente el mismo `value`: un botón con la opción elegida que abre un
   panel con buscador. La búsqueda no distingue acentos ni mayúsculas y toma las
   palabras en cualquier orden ("tablero pb" encuentra "TABLERO ... PB"). Enter
   elige la primera opción que coincide y Escape cierra. Mismo patrón de panel que
   SelectItemPto (Fix AM), que ya funciona dentro de los modales.

   options: [{ value, label, grupo?, buscar? }] — `grupo` agrega un encabezado; `buscar` suma texto al buscador.
   vacio: texto de la opción vacía (value ""); null = sin opción vacía.
   etiquetaActual: texto a mostrar si el value guardado no está entre las opciones. */
export const normalizar = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export const coincide = (texto, q) => {
  const t = normalizar(texto);
  return normalizar(q).split(/\s+/).filter(Boolean).every(p => t.includes(p));
};

export default function SelectBuscable({
  value, onChange, options = [], vacio = "— Seleccionar —", etiquetaActual = "",
  disabled = false, className = "", testId, placeholderBuscar = "Buscar...",
  envolver = false,   // Fix IU (2026-11-02): muestra completa la opción elegida, en varios renglones si hace falta
}) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);

  const actual = String(value ?? "");
  const elegido = options.find(o => String(o.value) === actual);
  const texto = elegido ? elegido.label : (actual ? (etiquetaActual || actual) : (vacio ?? ""));

  const abrir = () => {
    if (disabled) return;
    const r = btnRef.current.getBoundingClientRect();
    const alto = Math.min(360, window.innerHeight - 32);
    const cabeAbajo = window.innerHeight - r.bottom > alto + 8;
    const ancho = Math.max(r.width, 320);
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)),
      top: cabeAbajo ? r.bottom + 4 : Math.max(8, r.top - alto - 4),
      width: ancho, alto,
    });
    setQ("");
    setAbierto(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  // Como el <select>: solo avisa si la opción cambió.
  const elegir = (v) => { setAbierto(false); if (String(v) !== actual) onChange(String(v)); };
  // Fix HR (2026-10-23): `buscar` (opcional) = texto extra que el buscador también mira (cliente, N° de presupuesto...).
  const visibles = options.filter(o => !q.trim() || coincide(`${o.label} ${o.grupo || ""} ${o.buscar || ""}`, q));

  return (
    <>
      <button type="button" ref={btnRef} onClick={abrir} disabled={disabled} title={texto}
        className={`text-left flex ${envolver ? "items-start" : "items-center"} justify-between gap-2 disabled:bg-gray-100 disabled:text-gray-400 ${className}`}
        data-testid={testId}>
        <span className={`${envolver ? "min-w-0 whitespace-normal break-words leading-snug" : "truncate"} ${actual ? "text-gray-800" : "text-gray-500"}`}>{texto}</span>
        <span className="text-gray-400 flex-shrink-0">▾</span>
      </button>
      {abierto && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setAbierto(false)} />
          <div className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.alto }}
            data-testid={`${testId}-panel`}>
            <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100">
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); }
                  if (e.key === "Enter") { e.preventDefault(); if (visibles.length) elegir(visibles[0].value); }
                }}
                placeholder={placeholderBuscar} className="w-full text-xs outline-none"
                data-testid={`${testId}-buscar`} />
              <span className="text-[10px] text-gray-400 flex-shrink-0">{visibles.length}</span>
            </div>
            <div className="overflow-y-auto">
              {vacio !== null && !q.trim() && (
                <button type="button" onClick={() => elegir("")}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50"
                  data-testid={`${testId}-vacio`}>{vacio}</button>
              )}
              {visibles.length === 0 && <p className="px-3 py-3 text-xs text-gray-400 text-center">Sin resultados</p>}
              {visibles.map((o, i) => (
                <div key={`${o.grupo || ""}|${o.value}`}>
                  {o.grupo && (i === 0 || visibles[i - 1].grupo !== o.grupo) && (
                    <p className="px-3 py-1 text-[10px] font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB]">{o.grupo}</p>
                  )}
                  <button type="button" onClick={() => elegir(o.value)}
                    className={`w-full text-left px-3 py-1.5 text-xs whitespace-normal break-words hover:bg-[#EBF3FB] ${String(o.value) === actual ? "bg-emerald-50 font-semibold text-emerald-800" : "text-gray-700"}`}
                    data-testid={`${testId}-op`}>
                    {o.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>, document.body
      )}
    </>
  );
}
